import { useCallback } from "react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import useZipExport from "./useZipExport";

/**
 * Bulk-downloads assets as a single ZIP, built in a background Web Worker.
 *
 * Urls are always resolved server-side (`POST /assets/download-manifest`) rather
 * than read off the in-memory page: a selection in the assets editor can span
 * pages, and a folder download covers assets the current page (and any
 * subfolders) never loaded. A folder download recurses into subfolders
 * server-side; the manifest's `folders` path table is used to rebuild that
 * structure as nested groups inside the zip.
 */
export default function useAssetsDownload(showId) {
  const { startExport, cancelExport, exporting } = useZipExport();

  // `scope` is either { assetIds } or { folder } — see the manifest endpoint.
  // `extraFiles` are written into the archive as-is; see useZipExport.
  const download = useCallback(
    async (scope, zipName, extraFiles = []) => {
      if (!showId) return;

      let manifest;
      try {
        const res = await apiFetch(
          `/api/shows/${showId}/assets/download-manifest`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(scope),
          },
        );
        if (!res.ok) throw new Error("Failed to resolve downloads");
        manifest = await res.json();
      } catch (error) {
        console.error(error);
        toast.error("Could not prepare the download");
        return;
      }

      const files = manifest.files || [];
      if (files.length === 0) {
        toast.info("No downloadable assets found");
        return;
      }
      if (manifest.skipped > 0) {
        toast.warning(
          `${manifest.skipped} asset${manifest.skipped === 1 ? "" : "s"} skipped (no file attached)`,
        );
      }

      // `manifest.folders` maps each reachable subfolder to its path relative to
      // the requested scope (empty for the `assetIds` scope, which collapses
      // every file into a single flat group below).
      const pathByFolderId = new Map(
        (manifest.folders || []).map((f) => [f._id, f.path]),
      );
      const buckets = new Map(); // key: JSON.stringify(path)
      for (const f of files) {
        const path = pathByFolderId.get(f.folder ?? null) ?? [];
        const key = JSON.stringify(path);
        if (!buckets.has(key)) buckets.set(key, { path, items: [] });
        buckets.get(key).items.push({ url: f.url, fileName: f.name });
      }

      const name = zipName || "assets";
      startExport({
        groups: Array.from(buckets.values()),
        rootFolder: name,
        zipName: name,
        extraFiles,
      });
    },
    [showId, startExport],
  );

  const downloadAssets = useCallback(
    (assetIds, zipName, extraFiles) =>
      download({ assetIds }, zipName, extraFiles),
    [download],
  );

  // `folderId` of null means the show root.
  const downloadFolder = useCallback(
    (folderId, zipName) => download({ folder: folderId ?? null }, zipName),
    [download],
  );

  return { downloadAssets, downloadFolder, cancelExport, exporting };
}
